import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TELEPHONY_PROVIDER_DEFS,
  TEMPLATE_CHANNEL_DEFS,
  type TelephonyProviderKind,
  type TemplateChannel,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { Badge, Modal, PageHead } from '../../components/ui';
import { AgentLineField } from '../../components/AgentLine';
import { useToast } from '../../components/Toast';

/**
 * إعدادات مركز الاتصال — ثلاث بطاقات.
 *
 * **المزوّد**: أين يخرج الصوت. `NONE` هو الفعّال اليوم — الاتصال من جهاز
 * الموظف والدورة تُسجَّل في النظام. وبيانات مزوّد سحابي تُحفظ مشفَّرةً
 * لكن الشاشة تقولها بلا لفّ: النقل الصوتي لن يعمل حتى يُكتب تنفيذ
 * المزوّد ويُختبر على حساب فعلي. حفظُ البيانات تجهيزٌ لا تفعيل.
 *
 * **النتائج**: قائمة ما تنتهي به المكالمات — تحرّرها الإدارة كما طُلب.
 *
 * **القوالب**: رسائل جاهزة تُرسل أثناء المكالمة بنقرة — {{الاسم}} يُملأ
 * تلقائيًّا.
 */
export function CallCenterSettings() {
  return (
    <div className="page">
      <PageHead
        title="إعدادات مركز الاتصال"
        subtitle="المزوّد، ونتائج المكالمات، وقوالب الرسائل السريعة"
      />
      <div className="col" style={{ gap: 'var(--sp-4)' }}>
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">
              <Icon name="phone" size={16} /> خطّك في مركز الاتصال
            </h2>
          </div>
          <div className="card-body">
            <AgentLineField />
          </div>
        </div>
        <ProviderCard />
        <DispositionsCard />
        <RecordingCard />
        <TemplatesCard />
      </div>
    </div>
  );
}

/* ==========================================================================
   المزوّد
   ========================================================================== */

interface TelephonyConfig {
  provider: TelephonyProviderKind;
  callerId: string | null;
  accountRef: string | null;
  recordCalls: boolean;
  hasSecret: boolean;
  live: boolean;
}

function ProviderCard() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['cc', 'telephony'],
    queryFn: () => api<TelephonyConfig>('/callcenter/settings/telephony'),
  });

  const [provider, setProvider] = useState<TelephonyProviderKind>('NONE');
  const [callerId, setCallerId] = useState('');
  const [accountRef, setAccountRef] = useState('');
  const [secret, setSecret] = useState('');
  const [recordCalls, setRecordCalls] = useState(false);

  useEffect(() => {
    const c = config.data;
    if (!c) return;
    setProvider(c.provider);
    setCallerId(c.callerId ?? '');
    setAccountRef(c.accountRef ?? '');
    setRecordCalls(c.recordCalls);
  }, [config.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/callcenter/settings/telephony', {
        method: 'PATCH',
        body: {
          provider,
          callerId: callerId.trim() || null,
          accountRef: accountRef.trim() || null,
          secret: secret || undefined,
          recordCalls,
        },
      }),
    onSuccess: () => {
      toast('حُفظت الإعدادات');
      setSecret('');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'telephony'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">مزوّد الاتصالات</h2>
        {config.data && (
          <Badge kind={config.data.live ? 'success' : 'neutral'}>
            {config.data.live ? 'نقل صوتي مفعّل' : 'الاتصال من جهاز الموظف'}
          </Badge>
        )}
      </div>
      <div className="card-body">
        <div className="form-grid">
          <label className="field">
            <span className="label">المزوّد</span>
            <select
              className="select"
              value={provider}
              onChange={(e) => setProvider(e.target.value as TelephonyProviderKind)}
            >
              {TELEPHONY_PROVIDER_DEFS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="label">الرقم الظاهر للعميل</span>
            <input
              className="input num"
              dir="ltr"
              placeholder="+202xxxxxxx"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
            />
            <span className="tiny mute">رقم الشركة — لا الرقم الشخصي للموظف.</span>
          </label>

          {provider !== 'NONE' && (
            <>
              <label className="field">
                <span className="label">معرّف الحساب (SID)</span>
                <input
                  className="input num"
                  dir="ltr"
                  value={accountRef}
                  onChange={(e) => setAccountRef(e.target.value)}
                />
              </label>

              <label className="field">
                <span className="label">
                  المفتاح السري{config.data?.hasSecret ? ' — محفوظ، اتركه فارغًا لإبقائه' : ''}
                </span>
                <input
                  className="input num"
                  dir="ltr"
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="new-password"
                />
                <span className="tiny mute">يُحفظ مشفَّرًا ولا يُعرض مرة أخرى.</span>
              </label>
            </>
          )}
        </div>

        <label className="row mt-3" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={recordCalls}
            onChange={(e) => setRecordCalls(e.target.checked)}
          />
          <span className="small">تسجيل المكالمات صوتيًّا حين يتوفر النقل عبر المزوّد</span>
        </label>

        {/*
          طريق الصفر جنيه: فون لينك جسرُ البلوتوث الجاهز.
          المتصفح لا يبلغ صوت البلوتوث (HFP) بأي واجهة، لكن ويندوز يسلّم
          روابط tel: لتطبيق الاتصال الافتراضي — فإن كان فون لينك صار زرُّ
          «اتصال» عندنا مكالمةً من شريحة الموظف بصوتٍ في سماعة اللابتوب.
        */}
        {provider === 'NONE' && (
          <div className="cc-phonelink mt-4">
            <h3 className="section-title">
              الاتصال من اللابتوب عبر هاتف الموظف — بلا مزوّد وبلا تكلفة
            </h3>
            <ol className="cc-steps">
              <li>
                ثبّت تطبيق <strong>اتصال بالهاتف (Phone Link)</strong> على ويندوز، واقرن
                هاتف الموظف بالبلوتوث من داخله.
              </li>
              <li>
                في إعدادات ويندوز ← التطبيقات ← التطبيقات الافتراضية، اجعل{' '}
                <strong>Phone Link</strong> هو تطبيق روابط الاتصال <code dir="ltr">TEL</code>.
              </li>
              <li>
                انتهى: زر «اتصال» في الكونسول يفتح المكالمة من شريحة الهاتف والصوت في
                سماعة اللابتوب — والنظام يسجّل التعرّف والمؤقّت والنتيجة وملف العميل.
              </li>
              <li>
                <strong>بلا خطوات زائدة:</strong> من تطبيق GoyOne لسطح المكتب يُطلب الهاتف
                المقترن مباشرة — الضغطة تتّصل، والكتم والانتظار والإقفال من الشاشة، وإقفال
                المكالمة من الهاتف يفتح نافذة النتيجة وحدها. ولا حاجة لجعل فون لينك تطبيق{' '}
                <code dir="ltr">TEL</code> الافتراضي حينها.
              </li>
            </ol>
            <p className="tiny mute">
              وبما أن الشركة توزّع هواتفها وخطوطها على الموظفين، فالرقم الظاهر للعميل
              رقمُ شركةٍ فعلًا — وكل موظف يسجّل خطّه في الكونسول فيظهر في التقارير.
              يبقى حدّان: لا تسجيل صوتيًّا للمكالمة، والواردة تُسجَّل بزر «سجّل
              واردة» — ومن احتاجهما يومًا فطريقهما مزوّد سحابي برقم موحّد.
            </p>
          </div>
        )}

        {provider !== 'NONE' && (
          <div className="alert alert-info mt-4">
            بياناتك تُحفظ مشفَّرة، لكن النقل الصوتي عبر {provider} <strong>غير مفعّل بعد</strong>:
            تفعيله يحتاج كتابة تكامل المزوّد واختباره على حسابك الفعلي. إلى حينها يبقى
            الاتصال من جهاز الموظف والدورة كاملة مسجَّلة في النظام. جهّز الحساب وأخبرنا
            لنُكمل الربط.
          </div>
        )}

        <div className="row mt-4">
          <button
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            حفظ
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   النتائج
   ========================================================================== */

interface Disposition {
  id: string;
  key: string | null;
  label: string;
  kind: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  suggestsFollowUp: boolean;
  isActive: boolean;
  order: number;
}

function DispositionsCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Disposition | 'new' | null>(null);

  const dispositions = useQuery({
    queryKey: ['cc', 'dispositions'],
    queryFn: () => api<{ items: Disposition[] }>('/callcenter/dispositions'),
  });

  const toggle = useMutation({
    mutationFn: (d: Disposition) =>
      api(`/callcenter/dispositions/${d.id}`, {
        method: 'PATCH',
        body: {
          label: d.label,
          kind: d.kind,
          suggestsFollowUp: d.suggestsFollowUp,
          isActive: !d.isActive,
          order: d.order,
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cc', 'dispositions'] }),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التعديل'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">نتائج المكالمات</h2>
        <button className="btn btn-sm btn-primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={14} />
          نتيجة جديدة
        </button>
      </div>
      <div className="card-body">
        <p className="tiny mute mb-3">
          ما يختاره الموظف عند إنهاء كل مكالمة. الموقوفة تبقى في المكالمات القديمة
          وتختفي من نافذة الإنهاء — تاريخُ نتيجةٍ لا يُمحى.
        </p>
        <div className="pick-list">
          {(dispositions.data?.items ?? []).map((d) => (
            <div key={d.id} className="pick-row" style={{ cursor: 'default' }}>
              <Badge kind={d.kind}>{d.label}</Badge>
              {d.suggestsFollowUp && <span className="tiny mute">تقترح متابعة</span>}
              {!d.isActive && <Badge kind="neutral">موقوفة</Badge>}
              <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => setEditing(d)}
                  aria-label={`تعديل ${d.label}`}
                >
                  <Icon name="edit" size={14} />
                </button>
                <button
                  className="btn btn-sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(d)}
                >
                  {d.isActive ? 'أوقف' : 'فعّل'}
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <DispositionDialog
          disposition={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function DispositionDialog({
  disposition,
  onClose,
}: {
  disposition: Disposition | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(disposition?.label ?? '');
  const [kind, setKind] = useState(disposition?.kind ?? 'neutral');
  const [suggestsFollowUp, setSuggestsFollowUp] = useState(
    disposition?.suggestsFollowUp ?? false,
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(
        disposition
          ? `/callcenter/dispositions/${disposition.id}`
          : '/callcenter/dispositions',
        {
          method: disposition ? 'PATCH' : 'POST',
          body: {
            label: label.trim(),
            kind,
            suggestsFollowUp,
            isActive: disposition?.isActive ?? true,
            order: disposition?.order ?? 99,
          },
        },
      ),
    onSuccess: () => {
      toast(disposition ? 'عُدّلت النتيجة' : 'أُضيفت النتيجة');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'dispositions'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  return (
    <Modal
      title={disposition ? `تعديل «${disposition.label}»` : 'نتيجة جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={label.trim().length < 2 || save.isPending}
            onClick={() => save.mutate()}
          >
            حفظ
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <label className="field">
        <span className="label">الاسم</span>
        <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>

      <label className="field">
        <span className="label">اللون</span>
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value as Disposition['kind'])}
        >
          <option value="success">أخضر — نتيجة إيجابية</option>
          <option value="info">أزرق — محايدة للأمام</option>
          <option value="warning">أصفر — تحتاج انتباهًا</option>
          <option value="danger">أحمر — سلبية</option>
          <option value="neutral">رمادي — إجرائية</option>
        </select>
      </label>

      <label className="row mt-2" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={suggestsFollowUp}
          onChange={(e) => setSuggestsFollowUp(e.target.checked)}
        />
        <span className="small">تقترح متابعة — تملأ موعد المتابعة تلقائيًّا عند اختيارها</span>
      </label>
    </Modal>
  );
}

/* ==========================================================================
   القوالب
   ========================================================================== */

interface Template {
  id: string;
  name: string;
  channel: TemplateChannel;
  subject: string | null;
  body: string;
  isActive: boolean;
  order: number;
}

function TemplatesCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Template | 'new' | null>(null);

  const templates = useQuery({
    queryKey: ['cc', 'templates'],
    queryFn: () => api<{ items: Template[] }>('/callcenter/templates'),
  });

  const toggle = useMutation({
    mutationFn: (t: Template) =>
      api(`/callcenter/templates/${t.id}`, {
        method: 'PATCH',
        body: {
          name: t.name,
          channel: t.channel,
          subject: t.subject,
          body: t.body,
          isActive: !t.isActive,
          order: t.order,
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cc', 'templates'] }),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التعديل'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">قوالب الرسائل السريعة</h2>
        <button className="btn btn-sm btn-primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={14} />
          قالب جديد
        </button>
      </div>
      <div className="card-body">
        <p className="tiny mute mb-3">
          تُرسل أثناء المكالمة بنقرة: «ابعتلي العرض» تصير زرًّا. اكتب{' '}
          <code dir="ltr">{'{{الاسم}}'}</code> و<code dir="ltr">{'{{الشركة}}'}</code> فتُملأ
          ببيانات العميل تلقائيًّا.
        </p>
        <div className="pick-list">
          {(templates.data?.items ?? []).map((t) => (
            <div key={t.id} className="pick-row" style={{ cursor: 'default' }}>
              <Badge kind={t.channel === 'EMAIL' ? 'info' : 'success'}>
                {TEMPLATE_CHANNEL_DEFS.find((c) => c.key === t.channel)?.label}
              </Badge>
              <span className="pick-row-text">
                <strong>{t.name}</strong>
                <span className="tiny mute"> — {t.body.slice(0, 50)}</span>
              </span>
              {!t.isActive && <Badge kind="neutral">موقوف</Badge>}
              <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => setEditing(t)}
                  aria-label={`تعديل ${t.name}`}
                >
                  <Icon name="edit" size={14} />
                </button>
                <button
                  className="btn btn-sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(t)}
                >
                  {t.isActive ? 'أوقف' : 'فعّل'}
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <TemplateDialog
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TemplateDialog({
  template,
  onClose,
}: {
  template: Template | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(template?.name ?? '');
  const [channel, setChannel] = useState<TemplateChannel>(template?.channel ?? 'EMAIL');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(template ? `/callcenter/templates/${template.id}` : '/callcenter/templates', {
        method: template ? 'PATCH' : 'POST',
        body: {
          name: name.trim(),
          channel,
          subject: channel === 'EMAIL' ? subject.trim() || null : null,
          body: body.trim(),
          isActive: template?.isActive ?? true,
          order: template?.order ?? 99,
        },
      }),
    onSuccess: () => {
      toast('حُفظ القالب');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'templates'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  return (
    <Modal
      title={template ? `تعديل «${template.name}»` : 'قالب جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={name.trim().length < 2 || body.trim().length < 2 || save.isPending}
            onClick={() => save.mutate()}
          >
            حفظ
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <div className="form-grid">
        <label className="field">
          <span className="label">اسم القالب</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="field">
          <span className="label">القناة</span>
          <select
            className="select"
            value={channel}
            onChange={(e) => setChannel(e.target.value as TemplateChannel)}
          >
            {TEMPLATE_CHANNEL_DEFS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {channel === 'EMAIL' && (
        <label className="field">
          <span className="label">الموضوع</span>
          <input
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
      )}

      <label className="field">
        <span className="label">النص</span>
        <textarea
          className="textarea"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="أهلًا {{الاسم}}…"
        />
      </label>
    </Modal>
  );
}

/* ==========================================================================
   متابعة العمل — الإلزام والمراقبة إلى Drive
   ========================================================================== */

interface MonitorConfig {
  requireCallRecording: boolean;
  mode: 'OFF' | 'SCREENSHOTS' | 'VIDEO';
  screenshotEverySec: number;
  driveFolderId: string | null;
  driveClientId: string | null;
  hasClientSecret: boolean;
  hasRefreshToken: boolean;
  driveReady: boolean;
}

/* ==========================================================================
   تسجيل المكالمات — ثنائيّ: كلّها أو لا
   ========================================================================== */

interface RecordingCfg {
  callRecording: boolean;
  mode: string;
  screenshotEverySec: number;
  driveReady: boolean;
}

function RecordingCard() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['cc', 'monitoring'],
    queryFn: () => api<RecordingCfg>('/callcenter/settings/monitoring'),
  });

  const save = useMutation({
    mutationFn: (callRecording: boolean) =>
      api('/callcenter/settings/monitoring', { method: 'PATCH', body: { callRecording } }),
    onSuccess: () => {
      toast('حُفظ إعداد التسجيل');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'monitoring'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const on = config.data?.callRecording ?? false;

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">تسجيل المكالمات</h2>
        <Badge kind={on ? 'success' : 'neutral'}>{on ? 'كل المكالمات تُسجَّل' : 'بلا تسجيل'}</Badge>
      </div>
      <div className="card-body">
        {/*
          ثنائيّ عمدًا: إمّا تُسجَّل كل مكالمة أو لا شيء. «بعض المكالمات»
          بابٌ للانتقاء والنسيان، والغرض من التسجيل أن يكون سجلًّا كاملًا.
          حين يُفعَّل تُقفل لوحة الاتصال حتى يفتح الموظف جلسة التسجيل، فلا
          مكالمة تبدأ بلا تسجيلها.
        */}
        <div className="row" style={{ gap: 'var(--sp-2)' }}>
          <button
            className={`btn${!on ? ' btn-primary' : ''}`}
            disabled={save.isPending}
            onClick={() => save.mutate(false)}
          >
            بلا تسجيل
          </button>
          <button
            className={`btn${on ? ' btn-primary' : ''}`}
            disabled={save.isPending}
            onClick={() => save.mutate(true)}
          >
            تسجيل كل المكالمات
          </button>
        </div>
        <p className="tiny mute mt-3">
          التسجيل الصوتي يُحفظ في ملف العميل. لمراقبة الشاشة (لقطات أو فيديو) راجع{' '}
          <Link to="/settings/monitoring" className="link">مركز المراقبة</Link>.
        </p>
      </div>
    </div>
  );
}
